// University of Wisconsin-Madison: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UW-Madison's Common Data Set 2025-2026, published by Data, Academic
//    Planning & Institutional Research as an Excel file on Box (the SOURCES
//    url is the data.wisc.edu page that links it; the file's Answer Sheet
//    tab was read): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table ("Nonresidents" there is the international row), C8 test
//    policy for Fall 2027, C21/C22 early plans. The CDS gives no early action
//    counts. Its G1/G5 costs are not used: the aid office's 2026-27 budget is
//    newer and itemized for residents and nonresidents.
//  - admissions.wisc.edu: Deadlines and Decisions, Apply as a First-Year
//    Student (application opened for 2027, testing, fee, majors), What We
//    Look for in Our Applicants (the test-score waiver runs through the spring
//    2028 term), Direct Entry Programs. No decision dates beyond the "on or
//    before" release dates are published.
//  - Office of Student Financial Aid, Cost of Attendance: the 2026-27
//    estimated undergraduate budget (nine months). Its items sum to its
//    totals.
//  - College Scorecard, school id 240444 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UW-Madison Common Data Set 2025-2026',
        url: 'https://data.wisc.edu/common-data-set-and-rankings/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UW-Madison Admissions, Deadlines and Decisions',
        url: 'https://admissions.wisc.edu/deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UW-Madison Admissions, Apply as a First-Year Student',
        url: 'https://admissions.wisc.edu/apply-as-a-freshman/',
        read: '2026-10-01',
    },
    lookFor: {
        label: 'UW-Madison Admissions, What We Look for in Our Applicants',
        url: 'https://admissions.wisc.edu/can-i-get-in-to-uw-madison/',
        read: '2026-10-01',
    },
    directEntry: {
        label: 'UW-Madison Admissions, Direct Entry Programs',
        url: 'https://admissions.wisc.edu/direct-entry/',
        read: '2026-10-01',
    },
    costs: {
        label: 'UW-Madison Office of Student Financial Aid, Cost of Attendance',
        url: 'https://financialaid.wisc.edu/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?240444-University-of-Wisconsin-Madison',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-wisconsin-madison',
    kbId: 'university_of_wisconsin_madison',
    name: 'University of Wisconsin-Madison',
    shortName: 'UW-Madison',
    aliases: ['UW', 'Wisconsin'],
    location: 'Madison, WI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UW-Madison brand colors page); decoration only.
    branding: {
        primary: '#C5050C',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_wisconsin_madison_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 73912,
        admitted: 30167,
        enrolled: 8495,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10058, admitted: 5672 },
            { group: 'Out-of-state', applied: 49463, admitted: 19948 },
            { group: 'International', applied: 14391, admitted: 4547 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UW-Madison\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school, college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UW-Madison has no early decision plan. Its early action plan is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted in it, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to the university as a whole and name a first and a second choice major. Some programs admit students directly:',
        rows: [
            { school: 'Wisconsin School of Business', requirement: 'List a business major as your first choice. Your admission letter says whether you were admitted directly to the School of Business; if not, you are admitted as pre-business in Letters & Science and can apply to the BBA program later.' },
            { school: 'College of Engineering', requirement: 'List an engineering major as your first choice and a major outside engineering as your second. Math and science courses get special attention, and a letter from a math or science teacher is encouraged.' },
            { school: 'Dance', requirement: 'A separate application to the Dance Department and a passed audition. Pass before decisions are released and you are admitted to the major; otherwise you start as undeclared in the School of Education.' },
            { school: 'School of Music', requirement: 'A separate application to the School of Music and a passed audition. Pass before decisions are released and you are admitted as a music major; otherwise you start as undeclared in Letters & Science.' },
        ],
        note: 'UW-Madison encourages dance and music applicants to apply in early action.',
        source: 'directEntry',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'On or before January 31', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'On or before March 31', binding: false },
        ],
        note: 'Applications opened August 1. Your other materials are due November 9 for early action and January 22 for regular decision, by 11:59 p.m. Pacific Time. Early action is not binding; you commit by May 1. Some early action applicants are deferred, with final decisions by the end of March. You apply with the Common App or the Universities of Wisconsin Application, and the fee is $80.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending ACT or SAT scores is optional for students applying for terms through spring 2028, and you will not be disadvantaged without them. You choose on the application whether to include scores, and that choice is final. UW-Madison considers only your top composite score and does not superscore.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 22, 2027.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'The Board of Regents\' waiver of the ACT or SAT requirement covers applicants through the spring 2028 term, whose deadline is October 1, 2027. UW-Madison has not yet said what applies to students entering in fall 2028.', source: 'lookFor' },
        ],
        source: 'lookFor',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [12532, 45518] },
            { item: 'Course materials and supplies', amounts: [700, 700] },
            { item: 'Housing and meals', amounts: [14994, 14994] },
            { item: 'Personal expenses', amounts: [2618, 2618] },
            { item: 'Transportation', amounts: [612, 1222] },
            { item: 'Loan fees', amounts: [68, 68] },
        ],
        totals: [31524, 65120],
        note: 'For an undergraduate over the nine-month academic year. First-year students also pay a new student fee of about $330, and UW-Madison adds $1,100 to the first-year supplies budget for a computer. Engineering, nursing and business add tuition differentials. Minnesota residents have their own budget: $37,574.',
        source: 'costs',
        netPrice: {
            average: 16600,
            byIncome: [
                { income: '$0 to $30,000', amount: 3424 },
                { income: '$30,001 to $48,000', amount: 3837 },
                { income: '$48,001 to $75,000', amount: 6026 },
                { income: '$75,001 to $110,000', amount: 15839 },
                { income: 'Over $110,000', amount: 28208 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UW-Madison\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
